import { Box, Button, FormControlLabel, IconButton, Radio, RadioGroup, TextField, Typography } from '@mui/material';
import React, { FC, RefObject, useEffect, useRef, useState, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import AcceptIcon from '../../../assets/icons/accept-icon';
import CancelIcon from '../../../assets/icons/cancel-icon';
import ConfigurationFilesLibrary from '../../../services/config-files-library';
import { getConfigFilesRequestBody } from '../../../services/types';
import { AppState } from '../../../store';
import { enqueueSnackbarAction } from '../../../store/snackbar/actions';
import {
  removeSelectedRows,
  uploadSoftwareFiles,
} from '../../../store/software-library-management/actions';
import { softwareConfigFileAlreadyExistsMessage } from '../messages';
import {
  LibraryManagementActionNames,
  SoftwareFilesRecord,
} from '../../../store/software-library-management/types';
import { softwareHeadCellsAccessors } from '../../bulk-device-management/queue-file/head-cells';
import Table from '../../bulk-device-management/table';
import FileUploader from '../../file-loader';
import { softwareFilesHeadCells, softwareModemFilesHeadCells } from './head-cells';
import useStyles from './styles';
import { setModalOpen } from '../../../store/modal/actions';
import { RemoteFlashActionNames } from '../../../store/configure/remote-flash/types';

const setPercentUpload = () => {};

const SoftwareFilesSection: FC<{}> = () => {
  const [columnVisibility, setColumnVisibility] = useState([]);
  const [columnOrder, setColumnOrder] = useState([]);
  const tableContainer: RefObject<HTMLDivElement> = useRef(null);
  const [selectedRows, setSelectedRows] = useState([]);
  const tableData = useSelector((state: AppState) => state.libraryManagement.softwareFiles);
  const { client, envVariables } = useSelector((state: AppState) => state.login);
  const fileExistsInDatabase = useSelector(
    (state: AppState) => state.firmware.softwareFileExistsInDb,
  );
  const [softwareFileUploadData, setSoftwareFileUploadData] = useState<any>({});
  const [fileObject, setFileObject] = useState<any>();
  const [partNumber, setPartNumber] = useState<string>('');
  const [value, setValue] = useState<string>('fota');
  const classes = useStyles();
  const dispatch = useDispatch();

  const fotaKey=value.includes('fota');
  const TableData = {
    title: 'Software Files',
    storageString: 'library-management-software-files',
    headerColumns: value.includes('fota') ? softwareFilesHeadCells : softwareModemFilesHeadCells ,
    sortableAccessors: softwareHeadCellsAccessors,
    onRowSelection: setSelectedRows,
    data: tableData,
    id: 'software-files',
    setColumnVisibility,
    columnVisibility,
    setColumnOrder,
    columnOrder,
    tableContainer,
  };

  const handleFile = (data: any) => {
    setFileObject(data);
  };
  const handleDeleteSelected = () => {
    dispatch(
      removeSelectedRows(tableData, selectedRows, (deletedIds: Array<string>) => {
        const filteredRows = tableData.filter((row) => !deletedIds.includes(row.id));
        dispatch({
          type: LibraryManagementActionNames.SET_SOFTWARE_FILES,
          payload: filteredRows,
        });
      }),
    );
  };

  const setTableData = useCallback(
    (data: any) => {
      dispatch({
        type: LibraryManagementActionNames.SET_SOFTWARE_FILES,
        payload: data,
      });
    },
    [dispatch],
  );

  const getConfigurationFiles = useCallback(async () => {
    const configFilesLibrary = new ConfigurationFilesLibrary(
      client,
      envVariables.configurationFilesLibraryV1BaseUrl,
    );
    const input: getConfigFilesRequestBody = { fileTypeId: '10030', isRetrievable: true, limit: 500 };
    const response = await configFilesLibrary.getConfigFiles(input);
    const configFilesLibraries = response.data.configs;
    if (configFilesLibraries) {
      setTableData(
        configFilesLibraries.map((configFile) => ({
          ...configFile,
          userId: configFile.lastUpdatedByUserId,
        })),
      );
    } else {
      setTableData(null);
    }
  }, [client, envVariables.configurationFilesLibraryV1BaseUrl, setTableData]);

  const getConfigurationModemFiles = async () => {
    const configFilesLibrary = new ConfigurationFilesLibrary(
      client,
      envVariables.configurationFilesLibraryV1BaseUrl,
    );
    const input: getConfigFilesRequestBody = { fileTypeId: '10348', isRetrievable: true, limit: 500 };
    try {
      const response = await configFilesLibrary.getConfigFiles(input);
      const configFilesLibraries = response.data.configs;
      if (configFilesLibraries) {
        setTableData(
          configFilesLibraries.map((configFile) => ({
            ...configFile,
            userId: configFile.lastUpdatedByUserId,
          })),
        );
      } else {
        setTableData(null);
      }
    } catch (error: any) {
      if (error?.response?.status === 404) {
        setTableData(null);
      }
    }
  }

  const handleAccept = async () => {
    const fileTypeId=fotaKey?'10030':'10348';
    const fileUploadData = {
      file: fileObject,
      fileTypeId,
      partNumber,
      setPercentUpload,
      finishedCallback: (response: SoftwareFilesRecord) => {
        setFileObject(null);
        setPartNumber('');
        dispatch(
          enqueueSnackbarAction({
            message: 'Software File upload successful',
            options: {
              variant: 'success',
            },
          }),
        );
        dispatch({
          type: LibraryManagementActionNames.SET_SOFTWARE_FILES,
          payload: [response, ...tableData],
        });
        if (fotaKey) getConfigurationFiles();
        else getConfigurationModemFiles();
      },
      overwrite: false,
    };
    setSoftwareFileUploadData(fileUploadData);
    dispatch(uploadSoftwareFiles(fileUploadData));
  };
  const handleCancel = () => {
    setFileObject(null);
  };

  const handleSoftwareFileOverwrite = (dispatch: any) => {
    const replaceFileData = { ...softwareFileUploadData, overwrite: true };
    const existing = tableData.find(item => item.id === (fileExistsInDatabase as any).hash)
    if(existing) {
      dispatch(
        setModalOpen({
          open: true,
          message: softwareConfigFileAlreadyExistsMessage,
          accept: () => {
            dispatch(uploadSoftwareFiles(replaceFileData));
            setFileObject(null);
            dispatch({
              type: RemoteFlashActionNames.SET_FILE_EXISTS_IN_DB,
              payload: false,
            });
          },
          cancel: () => {
            dispatch({
              type: RemoteFlashActionNames.SET_FILE_EXISTS_IN_DB,
              payload: false,
            });
          },
        }),
      );
    } else {
      dispatch(uploadSoftwareFiles(replaceFileData));
    }
  };

  useEffect(() => {
    if (fileExistsInDatabase) {
      handleSoftwareFileOverwrite(dispatch);
    }
  }, [fileExistsInDatabase, dispatch]);

  const handlePartNumber = (event: any) => {
    setPartNumber(event.target.value);
  };

  useEffect(() => {
    getConfigurationFiles();
  }, [getConfigurationFiles]);

  const handleChange = (event: any) => {
    const updateValue = event.target.value;
    setValue(updateValue);
    if (updateValue.includes('modemFlash')) {
      getConfigurationModemFiles();
    } else {
      getConfigurationFiles();
    }
  }

  return (
    <div className={classes.scrollBox}>
      <div>
        <Box>
          <RadioGroup
            row
            aria-labelledby="demo-controlled-radio-buttons-group"
            name="controlled-radio-buttons-group"
            value={value}
            onChange={handleChange}
          >
            <FormControlLabel value="fota" control={<Radio />} label="FOTA" />
            <FormControlLabel value="modemFlash" control={<Radio />} label="Modem Flash" />
          </RadioGroup>
        </Box>
      </div>
      <div className={classes.actionRow}>
        {fotaKey &&
          <>
            <Box p={1} pr={0}>
              <Typography variant="h6">Part Number:</Typography>
            </Box>

            <Box p={1} mt="-16px">
              <TextField
                variant="standard"
                className={classes.partNumber}
                margin="none"
                id="part-number-input"
                label="Part#"
                onChange={handlePartNumber}
                value={partNumber}
                required
              />
            </Box>
          </>
        }
        <Box>
          {fileObject ? <Box mb={1}>{fileObject.name}</Box> : null}
          <FileUploader
            text="Upload Software File"
            buttonId="software-files-upload"
            handleFile={handleFile}
          />
        </Box>
        {(fotaKey ?fileObject && partNumber: fileObject) && (
          <Box className={classes.confirm} display="flex" alignItems="center">
            <Typography>Are you sure?</Typography>
            <IconButton id={`${TableData.id}-confirm`} onClick={() => handleAccept()} size="large">
              <AcceptIcon />
            </IconButton>
            <IconButton
              id={`${TableData.id}-deny`}
              onClick={() => handleCancel()}
              aria-label="edit"
              size="large"
            >
              <CancelIcon />
            </IconButton>
          </Box>
        )}
        <Button
          className={classes.button}
          variant="outlined"
          size="small"
          onClick={() => handleDeleteSelected()}
          disabled={selectedRows.length === 0}
        >
          Delete Selected
        </Button>
      </div>
      <div ref={tableContainer} className={classes.tableContainer}>
        <Table {...TableData} />
      </div>
    </div>
  );
};

export default SoftwareFilesSection;
